import { MessageSquare, Phone, PhoneCall, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useDialer } from './dialer';
import { InCallActions } from './InCallActions';
import { useCallConversation, useLiveCalls, useSecondsSince } from './liveCallsContext';
import { usePhone } from './phone';

const clock = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${mins}:${String(rest).padStart(2, '0')}`;
};

/**
 * The live call, above the thread, on every tab.
 *
 * For a Twilio phone this module registers itself it is the only place an
 * agent can answer or hang up: it renders off the Device's own state rather
 * than the `call.*` socket events, because those describe what the SERVER
 * sees, and acting on them would offer an Answer button on a call ringing at
 * somebody else's desk.
 *
 * For a phone the DASHBOARD holds — the softphone frame, the RingCentral
 * dialer — it is a strip with no controls on it: who the agent is talking to,
 * for how long, on which line, and two ways onward: open the call's
 * conversation here, in the thread beneath, and bring the dashboard's panel
 * back on screen. The controls stay in that panel, which is on screen on
 * every dashboard page; a second set here would only drift from it. This
 * used to draw nothing at all for a hosted phone, and an agent working a
 * thread had no sign of the call over it.
 *
 * The RingCentral dialer this module mounts itself gets the same strip while a
 * call is up in the widget, with Show dialer when its panel is hidden — the
 * widget's own controls are the only ones that call has.
 *
 * Nothing is drawn while a phone is merely registered — an idle softphone is
 * not news, and an always-present bar would cost thread height on every screen.
 */
export function CallBar({ onOpenConversation }: { onOpenConversation: (displayId: number) => void }) {
  const phone = usePhone();
  const dialer = useDialer();
  const { find } = useLiveCalls();

  const twilioLive = phone !== null && (phone.status === 'ringing' || phone.status === 'active');
  const twilioConversation = useCallConversation({
    provider: 'twilio',
    // The line as well as the number: a customer reached on two of this
    // tenant's lines is two records, and the number alone picked whichever
    // came first.
    inboxId: twilioLive ? (phone.inbox?.id ?? null) : null,
    peer: twilioLive ? phone.peer : null,
    hint: twilioLive ? phone.conversationDisplayId : null,
  });

  const rcCall = dialer?.activeCall ?? null;
  const rcConversation = useCallConversation({
    provider: 'ringcentral',
    sessionKey: rcCall?.sessionKey ?? null,
    peer: rcCall?.peer ?? null,
    hint: rcCall?.conversationDisplayId ?? null,
  });
  const rcSeconds = useSecondsSince(rcCall?.since ?? null);
  const rcRecord = rcCall ? find({ provider: 'ringcentral', sessionKey: rcCall.sessionKey, peer: rcCall.peer }) : null;

  const twilio = phone ? twilioBar(phone, twilioConversation, onOpenConversation) : null;

  let ringcentral: ReactNode = null;
  if (dialer && rcCall) {
    const who = rcCall.name ?? rcRecord?.contactName ?? rcCall.peer ?? 'RingCentral call';
    const line = rcRecord?.inboxName ?? 'RingCentral';
    ringcentral = (
      <div className="callbar">
        <span className="callbar-icon">
          <PhoneCall size={14} />
        </span>
        <span className="callbar-who">{who}</span>
        <span className="callbar-sub">
          {rcCall.since !== null ? `${clock(rcSeconds)} · ` : 'On a call · '}
          {rcCall.name && rcCall.peer ? `${rcCall.peer} · ` : ''}
          {line}
        </span>
        <div className="callbar-actions">
          {rcConversation !== null && (
            <button type="button" className="cbtn" onClick={() => onOpenConversation(rcConversation)}>
              <MessageSquare size={13} />
              Open conversation
            </button>
          )}
          {!dialer.panelOpen && (
            <button type="button" className="cbtn" onClick={dialer.openPanel}>
              Show dialer
            </button>
          )}
        </div>
      </div>
    );
  } else if (dialer && !dialer.hosted && dialer.hasCall && !dialer.panelOpen) {
    // A call the widget has not described — ringing, or an older widget that
    // names nothing: still the way back to its controls.
    ringcentral = (
      <div className="callbar">
        <span className="callbar-icon">
          <Phone size={14} />
        </span>
        <span className="callbar-who">On a call in the RingCentral dialer</span>
        <span className="callbar-sub">{dialer.statusLine}</span>
        <div className="callbar-actions">
          <button type="button" className="cbtn" onClick={dialer.openPanel}>
            Show dialer
          </button>
        </div>
      </div>
    );
  }

  if (!twilio && !ringcentral) return null;
  return (
    <>
      {twilio}
      {ringcentral}
    </>
  );
}

function twilioBar(
  phone: NonNullable<ReturnType<typeof usePhone>>,
  conversation: number | null,
  onOpenConversation: (displayId: number) => void,
) {
  const { status, peer, outbound, seconds, micBlocked, error } = phone;

  if (micBlocked) {
    return (
      <div className="callbar callbar-warn">
        <span className="callbar-who">
          The browser blocked the microphone, so calls cannot connect.
        </span>
        <span className="callbar-sub">
          Allow it for this site, then try again. Inside the dashboard the frame itself must permit
          it.
        </span>
        <button type="button" className="cbtn" onClick={phone.retry}>
          Try again
        </button>
      </div>
    );
  }

  // A failed registration is worth saying once: it means inbound calls are not
  // ringing here at all, which is otherwise indistinguishable from a quiet day.
  if (status === 'error') {
    return (
      <div className="callbar callbar-warn">
        <span className="callbar-who">Calling is unavailable.</span>
        <span className="callbar-sub">{error ?? 'The phone connection failed.'}</span>
        <button type="button" className="cbtn" onClick={phone.retry}>
          Try again
        </button>
      </div>
    );
  }

  if (status !== 'ringing' && status !== 'active') return null;

  const ringingIn = status === 'ringing' && !outbound;
  const open =
    conversation !== null ? (
      <button
        type="button"
        className="cbtn"
        title="Open this call's conversation in the thread below"
        onClick={() => onOpenConversation(conversation)}
      >
        <MessageSquare size={13} />
        Open conversation
      </button>
    ) : null;

  return (
    <div className={`callbar${ringingIn ? ' callbar-ringing' : ''}`}>
      <span className="callbar-icon">
        <Phone size={14} />
      </span>
      <span className="callbar-who">{peer ?? 'Unknown number'}</span>
      <span className="callbar-sub">
        {ringingIn ? 'Incoming call' : status === 'active' ? clock(seconds) : 'Calling…'}
        {phone.inbox ? ` · ${phone.inbox.name}` : ''}
      </span>
      <div className="callbar-actions">
        {open}
        {phone.hosted ? (
          // The dashboard's panel holds the controls; this is the way back to it.
          <button type="button" className="cbtn" onClick={phone.showPanel}>
            Show phone
          </button>
        ) : ringingIn ? (
          <>
            <button type="button" className="cbtn cbtn-accept" onClick={phone.accept}>
              <Phone size={13} />
              Answer
            </button>
            <button type="button" className="cbtn cbtn-danger" onClick={phone.reject}>
              <X size={13} />
              Decline
            </button>
          </>
        ) : (
          // Mute, keypad, recording, transfer, add and hang up — the same
          // controls the dashboard's softphone frame draws, so the two never
          // disagree.
          <InCallActions phone={phone} />
        )}
      </div>
    </div>
  );
}
